class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/* ---------------------------------------------------------------------------
   Free-shipping progress bar (settings.show_shipping_bar)
   Keeps every [data-lc-ship] in sync with the real cart, so the fill grows on
   add and shrinks on remove instead of relying on the drawer re-rendering.
   Qualifying total mirrors snippets/lc-cart-qualifying-total.liquid: every
   line except automatically added gift lines.
--------------------------------------------------------------------------- */
(function () {
  if (window.lcShippingBar) return;
  window.lcShippingBar = true;

  var SELECTOR = '[data-lc-ship]';
  var lastTotal = null;
  var reapplyTimer = null;

  function bars() {
    return Array.prototype.slice.call(document.querySelectorAll(SELECTOR));
  }

  function formatMoney(cents) {
    var format = window.lcMoneyFormat || '${{amount}}';
    var value = Math.max(0, Math.round(cents));

    function build(decimalMark, thousandMark, places) {
      var parts = (value / 100).toFixed(places).split('.');
      var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandMark);
      return places > 0 ? whole + decimalMark + parts[1] : whole;
    }

    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (match, name) {
      switch (name) {
        case 'amount_no_decimals':
          return build('.', ',', 0);
        case 'amount_with_comma_separator':
          return build(',', '.', 2);
        case 'amount_no_decimals_with_comma_separator':
          return build(',', '.', 0);
        case 'amount_with_apostrophe_separator':
          return build('.', "'", 2);
        case 'amount_with_space_separator':
          return build(',', ' ', 2);
        case 'amount_no_decimals_with_space_separator':
          return build('.', ' ', 0);
        default:
          return build('.', ',', 2);
      }
    });
  }

  /* Only /cart.js and /cart/change.js return a whole cart. The /cart/add.js
     reply is just the added lines, so it must not be used as a total. */
  function fullCart(data) {
    return data && typeof data.item_count === 'number' && Array.isArray(data.items) ? data : null;
  }

  function qualifyingTotal(cart) {
    if (!cart || !cart.items) return null;
    return cart.items.reduce(function (sum, item) {
      var props = item.properties || {};
      if (props._lc_gift || props._lc_free_gift) return sum;
      var line = item.final_line_price;
      if (line === undefined || line === null) line = item.line_price || 0;
      return sum + line;
    }, 0);
  }

  function paint(total) {
    if (total === null || total === undefined) return;
    lastTotal = total;

    bars().forEach(function (bar) {
      var goal = parseInt(bar.getAttribute('data-threshold'), 10) || 0;
      var fill = bar.querySelector('[data-lc-ship-fill]');
      var text = bar.querySelector('[data-lc-ship-text]');
      var pct = goal > 0 ? Math.min(100, (total / goal) * 100) : 100;
      var left = goal - total;

      bar.setAttribute('data-total', total);
      bar.classList.toggle('is-unlocked', left <= 0);

      if (fill) {
        fill.style.width = Math.round(pct * 10) / 10 + '%';
        if (fill.parentElement) fill.parentElement.setAttribute('aria-valuenow', Math.round(pct));
      }

      if (text) {
        if (left > 0) {
          var template = bar.getAttribute('data-msg') || 'Spend [amount] more to get free shipping.';
          text.innerHTML =
            '<span class="free_shipping_notice">' +
            template.replace('[amount]', '<strong>' + formatMoney(left) + '</strong>') +
            '</span>';
        } else {
          var span = document.createElement('span');
          span.textContent = bar.getAttribute('data-msg-done') || 'FREE SHIPPING WILL BE APPLIED AT CHECKOUT';
          text.textContent = '';
          text.appendChild(span);
        }
      }
    });
  }

  function refresh() {
    var base = (window.routes && window.routes.cart_url) || '/cart';
    return fetch(base + '.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        paint(qualifyingTotal(cart));
      })
      .catch(function () {});
  }

  /* The drawer markup is swapped wholesale on cart changes. Re-apply the last
     known total to any freshly injected bar still showing a stale one. */
  function watch() {
    var scope = document.querySelector('cart-drawer') || document.body;
    if (!scope || scope.lcShipWatched) return;
    scope.lcShipWatched = true;

    new MutationObserver(function () {
      if (lastTotal === null) return;
      var stale = bars().some(function (bar) {
        return bar.getAttribute('data-total') !== String(lastTotal);
      });
      if (!stale) return;
      clearTimeout(reapplyTimer);
      reapplyTimer = setTimeout(function () {
        paint(lastTotal);
      }, 20);
    }).observe(scope, { childList: true, subtree: true });
  }

  function start() {
    watch();
    refresh();

    document.addEventListener('lc:cart:mutated', function () {
      refresh();
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        var cart = fullCart(event && event.cartData);
        if (cart) paint(qualifyingTotal(cart));
        else refresh();
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
